import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService, { ExportFlowError } from '../services/apiService';
import { ExportDestination, SummaryRef } from '../types';
import ExportWizard from './ExportWizard';
import ExportDestinationList from './ExportDestinationList';
import { rememberPraktijkdataConfigs } from '../utils/praktijkdataConfigs';
import { rememberEduVConfigs } from '../utils/eduVConfigs';
import { openHttpsUrl } from '../utils/exportFlow';
import { openPendingWindow } from '../utils/exportWebWindow';
import { downloadBlob } from '../utils/downloadFile';
import { summaryMarkdownToClipboardHtml, summaryMarkdownToClipboardMarkdown, summaryMarkdownToPlaintext } from '../utils/summaryClipboard';
import { logger } from '../utils/logger';

interface ShareDialogProps {
    isOpen: boolean;
    onClose: () => void;
    summaryContent: string;
    summaryTitle: string;
    recordingName: string;
    fileId: string;
    meetingType: string;
    /** template_uuid van het verslag; de verslag-endpoints vallen terug op meetingType */
    templateUuid?: string | null;
    /** Schrijft de autosave van de editor weg vóór een exportstap; false = niet gelukt */
    onBeforeExport?: () => Promise<boolean>;
}

enum TextFormat {
    MARKDOWN = 'markdown',
    PLAINTEXT = 'plaintext',
    RTF = 'rtf'
}

// De wizard voor één bestemming; web = meteen de webstap
interface ActiveExport {
    destination: ExportDestination;
    web: boolean;
    initialWindow?: Window | null;
}

// Platformsleutels alleen voor de instellingenpagina (medewerker per koppeling);
// de wizard zelf kent geen platformen
const SETTINGS_PLATFORMS = { praktijkdata: 'praktijkdata', eduV: 'edu_v' };

const ShareDialog: React.FC<ShareDialogProps> = ({
    isOpen,
    onClose,
    summaryContent,
    summaryTitle,
    recordingName,
    fileId,
    meetingType,
    templateUuid,
    onBeforeExport
}) => {
    const { t } = useTranslation();
    const [selectedFormat, setSelectedFormat] = useState<TextFormat>(TextFormat.RTF);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // Vluchtige bevestiging onderaan het scherm. Leeft los van de dialoog,
    // zodat de melding blijft staan nadat copyToClipboard onClose() aanroept.
    const [toast, setToast] = useState<string | null>(null);
    // Foutmelding inline in de dialoog (i.p.v. een browser-alert), zodat de
    // gebruiker kan lezen wat misging en het meteen opnieuw kan proberen.
    const [error, setError] = useState<string | null>(null);
    const [destinations, setDestinations] = useState<ExportDestination[]>([]);
    // De ref (uuid of naam) waarmee de bestemmingen kwamen; de stappen gebruiken dezelfde
    const [exportRef, setExportRef] = useState<string | null>(null);
    const [destinationsLoading, setDestinationsLoading] = useState(false);
    const [activeExport, setActiveExport] = useState<ActiveExport | null>(null);
    const destinationsSeq = useRef(0);

    const apiService = useMemo(() => new ApiService(), []);
    const summaryRef = useMemo<SummaryRef>(() => ({ meetingType, templateUuid }), [meetingType, templateUuid]);

    useEffect(() => {
        if (isOpen) {
            // Vers openen: altijd terug naar het gewone deelmenu.
            setActiveExport(null);
            setError(null);
            void loadDestinations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen verversen bij openen of andere opname/soort
    }, [isOpen, fileId, summaryRef]);

    // Toast vanzelf laten verdwijnen na twee seconden.
    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 2000);
        return () => clearTimeout(timer);
    }, [toast]);

    const loadDestinations = async () => {
        const seq = ++destinationsSeq.current;
        setDestinationsLoading(true);
        try {
            const { ref, destinations: list } = await apiService.getExportDestinations(fileId, summaryRef);
            if (seq !== destinationsSeq.current) return;
            setExportRef(ref);
            setDestinations(list);
            // Alleen id + naam onthouden, zodat de instellingenpagina de
            // medewerker-instelling per koppeling kan tonen
            const remembered = (key: string) => list
                .filter((destination) => destination.platform.key === key)
                .map((destination) => ({ id: String(destination.config_id), name: destination.name }));
            rememberPraktijkdataConfigs(remembered(SETTINGS_PLATFORMS.praktijkdata));
            rememberEduVConfigs(remembered(SETTINGS_PLATFORMS.eduV));
        } catch (loadError) {
            if (seq !== destinationsSeq.current) return;
            // 403: gedeelde opname zonder downloadrecht, dan is er geen exportsectie.
            // Ook andere fouten blokkeren de rest van het deelmenu niet.
            if (!(loadError instanceof ExportFlowError && loadError.status === 403)) {
                logger.error('Loading export destinations failed with status:', loadError instanceof ExportFlowError ? loadError.status : loadError);
            }
            setExportRef(null);
            setDestinations([]);
        } finally {
            if (seq === destinationsSeq.current) setDestinationsLoading(false);
        }
    };

    const startExport = (destination: ExportDestination, web: boolean) => {
        setError(null);
        // De webstap opent een venster; dat moet in deze klik, anders houdt de popupblokker het tegen
        const initialWindow = web ? openPendingWindow(t('exportWizard.web.windowTitle'), t('exportWizard.web.windowText')) : undefined;
        setActiveExport({ destination, web, initialWindow });
    };

    // Tekst-payload per formaat. Wordt gebruikt voor het native deelvenster
    // (alleen platte tekst) en als plain-text-fallback bij het klembord.
    const getContentForFormat = (format: TextFormat): string => {
        switch (format) {
            case TextFormat.MARKDOWN:
                return summaryMarkdownToClipboardMarkdown(summaryContent);
            case TextFormat.PLAINTEXT:
            case TextFormat.RTF:
                return summaryMarkdownToPlaintext(summaryContent);
            default:
                return summaryMarkdownToClipboardMarkdown(summaryContent);
        }
    };

    // De Web Share API (navigator.share) is op desktopbrowsers onbetrouwbaar:
    // Firefox desktop ondersteunt het niet en in andere browsers verschijnt er
    // vaak geen bruikbaar deelvenster. Alleen op touch-/mobiele apparaten gebruiken.
    const supportsNativeShare = (): boolean => {
        if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
            return false;
        }
        const coarsePointer =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches;
        return (navigator.maxTouchPoints || 0) > 0 && coarsePointer;
    };

    // Zet zowel opgemaakte HTML als platte tekst op het klembord. Een EPD-
    // richtveld pakt de HTML (nette opmaak); een plat tekstveld de fallback.
    // Bij oudere browsers zonder ClipboardItem valt het terug op platte tekst.
    const writeRichTextToClipboard = async (html: string, plain: string): Promise<void> => {
        const canWriteRich =
            typeof ClipboardItem !== 'undefined' &&
            typeof navigator.clipboard?.write === 'function';

        if (canWriteRich) {
            const item = new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plain], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([item]);
        } else {
            await navigator.clipboard.writeText(plain);
        }
    };

    const copyToClipboard = async () => {
        setError(null);
        try {
            if (selectedFormat === TextFormat.RTF) {
                // Opgemaakte tekst: HTML + platte tekst samen op het klembord.
                await writeRichTextToClipboard(
                    summaryMarkdownToClipboardHtml(summaryContent),
                    summaryMarkdownToPlaintext(summaryContent),
                );
            } else {
                await navigator.clipboard.writeText(getContentForFormat(selectedFormat));
            }

            // Show success message
            const formatNames = {
                [TextFormat.MARKDOWN]: t('shareDialog.formatNames.markdown'),
                [TextFormat.PLAINTEXT]: t('shareDialog.formatNames.plaintext'),
                [TextFormat.RTF]: t('shareDialog.formatNames.rtf')
            };

            setToast(t('shareDialog.copiedToClipboard', { format: formatNames[selectedFormat] }));
            onClose();
        } catch (error) {
            logger.error('Error copying to clipboard:', error);
            setError(t('shareDialog.errors.copyFailed'));
        }
    };

    const exportToPdf = async () => {
        setError(null);
        try {
            const blob = await apiService.downloadPdf(fileId, summaryRef);
            await downloadBlob(blob, `${getSafeFileName(recordingName)}.pdf`);

            onClose();
        } catch (error) {
            logger.error('Error exporting to PDF:', error);
            setError(t('shareDialog.errors.pdfFailed'));
        }
    };

    const exportToWord = async () => {
        setError(null);
        try {
            const blob = await apiService.downloadWord(fileId, summaryRef);
            await downloadBlob(blob, `${getSafeFileName(recordingName)}.docx`);

            onClose();
        } catch (error) {
            logger.error('Error exporting to Word:', error);
            setError(t('shareDialog.errors.wordFailed'));
        }
    };

    const shareToOtherApps = async () => {
        // Op desktop (geen betrouwbare native share) direct kopiëren naar het
        // klembord. Dat is voorspelbaar en geeft via copyToClipboard zichtbare
        // bevestiging, in plaats van een deelvenster dat stil faalt.
        if (!supportsNativeShare()) {
            await copyToClipboard();
            return;
        }

        try {
            await navigator.share({
                title: summaryTitle,
                text: getContentForFormat(selectedFormat),
            });
            onClose();
        } catch (error: any) {
            // Gebruiker heeft het deelvenster zelf geannuleerd → niets doen,
            // niet stilletjes naar het klembord kopiëren.
            if (error?.name === 'AbortError') {
                return;
            }
            logger.error('Error sharing:', error);
            // Echte fout → terugvallen op kopiëren naar klembord.
            await copyToClipboard();
        }
    };

    const getSafeFileName = (fileName: string): string => {
        if (!fileName) return 'verslag_samenvatting';

        // Datum in dag-maand-jaar formaat
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const dateStr = `${day}-${month}-${year}`;

        // Titel verwerken: kleine letters, zonder underscores/spaties
        let processedTitle = fileName
            .toLowerCase()
            .replace(/[<>:"/\\|?*]/g, '') // Verwijder ongeldige karakters
            .replace(/[_\s]+/g, '_') // Vervang underscores en spaties door enkele underscore
            .replace(/^_+|_+$/g, '') // Verwijder underscores aan begin en eind
            .trim();

        // Beperk lengte van titel
        const maxTitleLength = 40;
        if (processedTitle.length > maxTitleLength) {
            processedTitle = processedTitle.substring(0, maxTitleLength).replace(/_+$/, '');
        }

        // Als titel leeg is na verwerking, gebruik standaard naam
        if (!processedTitle) {
            processedTitle = 'verslag';
        }

        // Combineer volgens formaat: {dmy}_{titel}_samenvatting
        return `${dateStr}_${processedTitle}_samenvatting`;
    };

    // Blijf gemount zolang er nog een toast zichtbaar is, ook al is de dialoog
    // zelf al gesloten.
    if (!isOpen && !toast) return null;

    return (
        <>
        {isOpen && (
        // z-[9999]: boven de sticky editor-header (99), toolbar (98) en chat-
        // overlay (110+) van SummaryEditor; zelfde laag als de SupportingDocuments-modal.
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-base font-semibold text-gray-900">{t('common.share')}</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="notice error" role="alert" style={{ margin: '0 0 16px' }}>
                            <p>{error}</p>
                            <button type="button" onClick={() => setError(null)}>{t('common.close')}</button>
                        </div>
                    )}

                    {/* De exportwizard vervangt het deelmenu tot de gebruiker klaar is of teruggaat. */}
                    {activeExport && exportRef && (
                        <ExportWizard
                            key={activeExport.destination.config_id}
                            apiService={apiService}
                            fileId={fileId}
                            summaryRef={exportRef}
                            destination={activeExport.destination}
                            startWithWeb={activeExport.web}
                            initialWindow={activeExport.initialWindow}
                            onBack={() => setActiveExport(null)}
                            onClose={onClose}
                            onDestinationsStale={(message) => {
                                setActiveExport(null);
                                setError(message);
                                void loadDestinations();
                            }}
                            saveSummary={onBeforeExport}
                        />
                    )}

                    {!activeExport && (
                    <div className="space-y-3">
                        {/* Share Options */}
                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 1 1 0 000 2h1v11H5V5h1a1 1 0 000-2H6z" />
                                </svg>
                            }
                            title={t('shareDialog.options.copyTitle')}
                            subtitle={t('shareDialog.options.copySubtitle')}
                            onClick={copyToClipboard}
                        />

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                            }
                            title={t('shareDialog.options.pdfTitle')}
                            subtitle={t('shareDialog.options.pdfSubtitle')}
                            onClick={exportToPdf}
                        />

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                            }
                            title={t('shareDialog.options.wordTitle')}
                            subtitle={t('shareDialog.options.wordSubtitle')}
                            onClick={exportToWord}
                        />

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm8 5H8V8l2-2 2 2v2z" clipRule="evenodd" />
                                    <path d="M10 7L8 9h1v2h2V9h1l-2-2z" />
                                </svg>
                            }
                            title={t('shareDialog.options.shareTitle')}
                            subtitle={t('shareDialog.options.shareSubtitle')}
                            onClick={shareToOtherApps}
                        />

                        {/* Exporteer naar: de bestemmingen van /export/v2. Zonder bestemmingen
                            (of zonder downloadrecht: 403) is de sectie er niet. */}
                        {destinationsLoading && destinations.length === 0 && (
                            <div className="flex justify-center py-4" role="status" aria-label={t('exportWizard.loading')}>
                                <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                            </div>
                        )}
                        {destinations.length > 0 && exportRef && (
                            <section className="pt-4 border-t border-gray-200" aria-labelledby="share-export-heading">
                                <h3 id="share-export-heading" className="text-sm font-semibold text-gray-900 mb-3">{t('exportWizard.sectionTitle')}</h3>
                                <ExportDestinationList
                                    destinations={destinations}
                                    resolveIconUrl={(iconUrl) => {
                                        // Alleen iconen van de eigen backend
                                        const url = apiService.resolveBackendUrl(iconUrl);
                                        const backend = apiService.resolveBackendUrl('/');
                                        return url && backend && url.startsWith(backend) ? url : null;
                                    }}
                                    onStart={startExport}
                                    onOpenUrl={openHttpsUrl}
                                />
                            </section>
                        )}

                        {/* Advanced Options Toggle */}
                        <div className="pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="flex items-center justify-center w-full text-sm text-gray-600 hover:text-primary-600 transition-colors"
                            >
                                <span>{t('shareDialog.advanced')}</span>
                                <svg
                                    className={`w-4 h-4 ml-2 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        {/* Advanced Options */}
                        {showAdvanced && (
                            <div className="pt-4 border-t border-gray-200">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('shareDialog.formatLabel')}
                                        </label>
                                        <div className="flex space-x-2">
                                            {Object.values(TextFormat).map((format) => (
                                                <button
                                                    key={format}
                                                    onClick={() => setSelectedFormat(format)}
                                                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${selectedFormat === format
                                                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                                                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    {format === TextFormat.MARKDOWN && 'MD'}
                                                    {format === TextFormat.PLAINTEXT && t('shareDialog.formatButtons.plaintext')}
                                                    {format === TextFormat.RTF && 'RTF'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
        )}

        {toast && (
            <div className="notizy-toast-host">
                <div className="notizy-toast notizy-toast--success">
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {toast}
                </div>
            </div>
        )}
        </>
    );
};

interface ShareOptionProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    onClick: () => void;
}

const ShareOption: React.FC<ShareOptionProps> = ({ icon, title, subtitle, onClick }) => {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
        >
            <div className="flex-shrink-0 text-gray-600 mr-3">
                {icon}
            </div>
            <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm">{title}</div>
                <div className="text-gray-500 text-xs">{subtitle}</div>
            </div>
        </button>
    );
};

export default ShareDialog; 
